import React, { useState, useEffect } from 'react';
import './StaffTrainingTracker.css';
import * as XLSX from 'xlsx';

const StaffTrainingTracker = ({ currentUser }) => {
  const [staffTrainings, setStaffTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [trainingTypeFilter, setTrainingTypeFilter] = useState('all');
  
  // Date range filter states
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDate: '',
    endDate: '',
    type: 'all' // all, due, done
  });
  

  
  // New training form
  const [newTrainingForm, setNewTrainingForm] = useState({
    staffName: '',
    staffId: '',
    department: '',
    trainingDue: '',
    trainingDone: new Date().toISOString().split('T')[0],
    trainingNotRequired: false,
    trainingType: '',
    trainingProvider: '',
    certificateNumber: '',
    targetAudience: '',
    trainerName: '',
    doctorsCount: '',
    techniciansCount: '',
    engineersCount: '',
    nursingStaffCount: '',
    othersCount: '',
    totalParticipants: '',
    trainingHours: '',
    trainingManhours: '',
    trainingMandays: ''
  });

  useEffect(() => {
    loadStaffTrainings();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [staffTrainings, dateFilter, searchTerm, trainingTypeFilter, dateRangeFilter]);

  const loadStaffTrainings = () => {
    try {
      setLoading(true);
      setError(null);
      const savedTrainings = localStorage.getItem('staff-trainings');
      if (savedTrainings) {
        const parsedTrainings = JSON.parse(savedTrainings);
        // Filter out any null or undefined trainings
        const validTrainings = parsedTrainings.filter(training => training && typeof training === 'object');
        setStaffTrainings(validTrainings);
        setFilteredTrainings(validTrainings);
      } else {
        setStaffTrainings([]);
        setFilteredTrainings([]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading staff trainings:', err);
      setError('Failed to load staff trainings: ' + err.message);
      setLoading(false);
    }
  };

  const saveStaffTrainings = (newTrainings) => {
    try {
      localStorage.setItem('staff-trainings', JSON.stringify(newTrainings));
      setStaffTrainings(newTrainings);
      return true;
    } catch (error) {
      console.error('Failed to save staff trainings:', error);
      setError('Failed to save staff trainings: ' + error.message);
      return false;
    }
  };

  // Safely convert any value to string for searching
  const safeToString = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return value.toString();
    }
    // For objects and arrays, use JSON string representation
    try {
      return JSON.stringify(value);
    } catch (e) {
      return '';
    }
  };

  const applyFilters = () => {
    try {
      setError(null); // Clear any previous errors
      let filtered = [...staffTrainings];
      
      // Apply search filter - dynamic search across all fields
      if (searchTerm.trim() !== '') {
        const term = searchTerm.toLowerCase().trim();
        filtered = filtered.filter(training => {
          if (!training || typeof training !== 'object') return false;
          
          // Safely convert all fields to strings for searching
          const staffName = safeToString(training.staffName).toLowerCase();
          const staffId = safeToString(training.staffId).toLowerCase();
          const department = safeToString(training.department).toLowerCase();
          const trainingDue = safeToString(training.trainingDue).toLowerCase();
          const trainingDone = safeToString(training.trainingDone).toLowerCase();
          const trainingType = safeToString(training.trainingType).toLowerCase();
          const trainingProvider = safeToString(training.trainingProvider).toLowerCase();
          const certificateNumber = safeToString(training.certificateNumber).toLowerCase();
          const targetAudience = safeToString(training.targetAudience).toLowerCase();
          const trainerName = safeToString(training.trainerName).toLowerCase();
          
          // Search across all fields
          return (
            staffName.includes(term) ||
            staffId.includes(term) ||
            department.includes(term) ||
            trainingDue.includes(term) ||
            trainingDone.includes(term) ||
            trainingType.includes(term) ||
            trainingProvider.includes(term) ||
            certificateNumber.includes(term) ||
            targetAudience.includes(term) ||
            trainerName.includes(term)
          );
        });
      }
      
      // Apply training type filter
      if (trainingTypeFilter !== 'all') {
        filtered = filtered.filter(training => {
          if (!training || typeof training !== 'object') return false;
          return training.trainingType === trainingTypeFilter;
        });
      }
      
      // Apply date range filter
      if (dateRangeFilter.startDate || dateRangeFilter.endDate) {
        const startDate = dateRangeFilter.startDate ? new Date(dateRangeFilter.startDate) : null;
        const endDate = dateRangeFilter.endDate ? new Date(dateRangeFilter.endDate) : null;
        
        if (startDate) {
          startDate.setHours(0, 0, 0, 0);
        }
        if (endDate) {
          endDate.setHours(23, 59, 59, 999);
        }
        
        filtered = filtered.filter(training => {
          if (!training || typeof training !== 'object') return false;
          
          try {
            // Check Training Due date
            const trainingDueDate = parseDate(training.trainingDue);
            // Check Training Done date
            const trainingDoneDate = parseDate(training.trainingDone);
            
            // Apply filter based on type
            if (dateRangeFilter.type === 'due') {
              // Check if training due date is within range
              if (trainingDueDate && isDateInRange(trainingDueDate, startDate, endDate)) return true;
              return false;
            } else if (dateRangeFilter.type === 'done') {
              // Check if training done date is within range
              if (trainingDoneDate && isDateInRange(trainingDoneDate, startDate, endDate)) return true;
              return false;
            } else {
              // Check if any date is within range (all dates)
              if (trainingDueDate && isDateInRange(trainingDueDate, startDate, endDate)) return true;
              if (trainingDoneDate && isDateInRange(trainingDoneDate, startDate, endDate)) return true;
              return false;
            }
          } catch (dateError) {
            console.warn('Error processing date range filter for training:', training, dateError);
            return false;
          }
        });
      }
      
      // Apply date filter
      if (dateFilter !== 'all' && (!dateRangeFilter.startDate && !dateRangeFilter.endDate)) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        
        filtered = filtered.filter(training => {
          if (!training || typeof training !== 'object') return false;
          
          try {
            if (dateFilter === 'dueToday') {
              // Check if Training is due today
              const trainingDueDate = parseDate(training.trainingDue);
              if (trainingDueDate) {
                trainingDueDate.setHours(0, 0, 0, 0);
                if (trainingDueDate.getTime() === today.getTime()) return true;
              }
              
              return false;
            } else if (dateFilter === 'thisMonthDone') {
              // Check if Training is done this month
              const trainingDoneDate = parseDate(training.trainingDone);
              
              // Check Training Done
              if (trainingDoneDate && trainingDoneDate >= firstDayOfMonth && trainingDoneDate <= lastDayOfMonth) return true;
              
              return false;
            } else if (dateFilter === 'thisMonthDue') {
              // Check if Training is due this month
              const trainingDueDate = parseDate(training.trainingDue);
              
              // Check Training Due
              if (trainingDueDate && trainingDueDate >= firstDayOfMonth && trainingDueDate <= lastDayOfMonth) return true;
              
              return false;
            }
          } catch (dateError) {
            console.warn('Error processing date filter for training:', training, dateError);
            // Continue with other trainings even if one has date parsing issues
          }
          
          return dateFilter === 'all'; // If no specific date filter matches, include only if filter is 'all'
        });
      }
      
      setFilteredTrainings(filtered);
    } catch (err) {
      console.error('Error applying filters:', err);
      setError('Failed to apply filters: ' + err.message);
      // Still set filtered trainings to avoid breaking the UI
      setFilteredTrainings([...staffTrainings]);
    }
  };

  // Check if a date is within the specified range
  const isDateInRange = (date, startDate, endDate) => {
    if (!date) return false;
    
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    if (startDate && endDate) {
      return checkDate >= startDate && checkDate <= endDate;
    } else if (startDate) {
      return checkDate >= startDate;
    } else if (endDate) {
      return checkDate <= endDate;
    }
    return false;
  };

  // Parse date helper function
  const parseDate = (dateString) => {
    if (dateString === null || dateString === undefined || dateString === '' || 
        (typeof dateString === 'string' && dateString.trim() === '')) {
      return null;
    }
    
    try {
      // Convert to string if not already
      const str = typeof dateString === 'string' ? dateString : String(dateString);
      const trimmedStr = str.trim();
      
      // Handle DD-Mon-YY or DD-Mon-YYYY format (e.g., 25-Dec-12 or 25-Dec-2012)
      if (/^\d{1,2}-[A-Za-z]{3}-\d{2,4}$/.test(trimmedStr)) {
        const parts = trimmedStr.split('-');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                             'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthAbbr = parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase();
          const month = monthNames.indexOf(monthAbbr);
          
          if (month === -1) {
            console.warn('Invalid month in date string:', trimmedStr);
            return null;
          }
          
          let year = parseInt(parts[2], 10);
          if (isNaN(year)) {
            console.warn('Invalid year in date string:', trimmedStr);
            return null;
          }
          
          // Handle 2-digit years (assume 20XX if <= 50, 19XX if > 50)
          if (year < 100) {
            year = year <= 50 ? 2000 + year : 1900 + year;
          }
          
          if (isNaN(day) || day <= 0 || day > 31) {
            console.warn('Invalid day in date string:', trimmedStr);
            return null;
          }
          
          const date = new Date(year, month, day);
          // Validate that the date is valid
          if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
            return date;
          } else {
            console.warn('Invalid date (date overflow):', trimmedStr);
            return null;
          }
        }
      }
      
      // Try standard date parsing for other formats
      const date = new Date(trimmedStr);
      if (!isNaN(date.getTime())) {
        return date;
      }
    } catch (error) {
      console.warn('Error parsing date:', dateString, error);
    }
    
    return null;
  };

  // Get training dashboard statistics
  const getDashboardStats = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const firstDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const lastDayOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 2, 0);
    
    // Initialize training statistics
    let trainingStats = {
      dueToday: 0,
      thisMonthDone: 0,
      thisMonthDue: 0,
      nextMonthDue: 0,
      completed: 0,
      futureDue: 0,
      overdue: 0,
      notRequired: 0,
      byType: {},
      byDepartment: {}
    };
    
    // Initialize participant statistics
    let participantStats = {
      totalParticipants: 0,
      doctors: 0,
      technicians: 0,
      engineers: 0,
      nursingStaff: 0,
      others: 0
    };
    
    staffTrainings.forEach(training => {
      if (!training || typeof training !== 'object') return;
      
      try {
        // Process Training dates
        const trainingDueDate = parseDate(training.trainingDue);
        const trainingDoneDate = parseDate(training.trainingDone);
        const trainingType = training.trainingType || 'Unknown';
        const department = training.department || 'Unknown';
        
        // Initialize training type counter if not exists
        if (!trainingStats.byType[trainingType]) {
          trainingStats.byType[trainingType] = {
            total: 0,
            dueToday: 0,
            completed: 0,
            overdue: 0
          };
        }
        
        // Initialize department counter if not exists
        if (!trainingStats.byDepartment[department]) {
          trainingStats.byDepartment[department] = {
            total: 0,
            dueToday: 0,
            completed: 0,
            overdue: 0
          };
        }
        
        // Add participant counts
        participantStats.doctors += Number(training.doctorsCount) || 0;
        participantStats.technicians += Number(training.techniciansCount) || 0;
        participantStats.engineers += Number(training.engineersCount) || 0;
        participantStats.nursingStaff += Number(training.nursingStaffCount) || 0;
        participantStats.others += Number(training.othersCount) || 0;
        participantStats.totalParticipants += Number(training.totalParticipants) || 0;
        
        // Increment total for this training type and department
        trainingStats.byType[trainingType].total++;
        trainingStats.byDepartment[department].total++;
        
        // Check if training is not required for this staff
        if ((!trainingDueDate || trainingDueDate === '') && 
            (!trainingDoneDate || trainingDoneDate === '')) {
          trainingStats.notRequired++;
          trainingStats.byType[trainingType].completed++; // Count as completed if not required
          trainingStats.byDepartment[department].completed++; // Count as completed if not required
        }
        
        // Training Due Today
        if (trainingDueDate) {
          trainingDueDate.setHours(0, 0, 0, 0);
          if (trainingDueDate.getTime() === today.getTime()) {
            trainingStats.dueToday++;
            trainingStats.byType[trainingType].dueToday++;
            trainingStats.byDepartment[department].dueToday++;
          }
          // Training Overdue (before today and not completed)
          else if (trainingDueDate < today && 
                  (!trainingDoneDate || trainingDoneDate === '' || trainingDoneDate < trainingDueDate)) {
            trainingStats.overdue++;
            trainingStats.byType[trainingType].overdue++;
            trainingStats.byDepartment[department].overdue++;
          }
        }
        
        // Training This Month Done
        if (trainingDoneDate && trainingDoneDate >= firstDayOfMonth && trainingDoneDate <= lastDayOfMonth) {
          trainingStats.thisMonthDone++;
        }
        
        // Training This Month Due
        if (trainingDueDate && trainingDueDate >= firstDayOfMonth && trainingDueDate <= lastDayOfMonth) {
          trainingStats.thisMonthDue++;
        }
        
        // Training Next Month Due
        if (trainingDueDate && trainingDueDate >= firstDayOfNextMonth && trainingDueDate <= lastDayOfNextMonth) {
          trainingStats.nextMonthDue++;
        }
        
        // Training Completed (has done date)
        if (trainingDoneDate && !isNaN(trainingDoneDate.getTime())) {
          trainingStats.completed++;
          trainingStats.byType[trainingType].completed++;
          trainingStats.byDepartment[department].completed++;
        }
        
        // Training Future Due (after today)
        if (trainingDueDate && trainingDueDate > today) {
          trainingStats.futureDue++;
        }
      } catch (error) {
        console.warn('Error processing training for dashboard stats:', training, error);
      }
    });
    
    return {
      training: trainingStats,
      participants: participantStats
    };
  };

  // Clear date range filter
  const clearDateRangeFilter = () => {
    setDateRangeFilter({
      startDate: '',
      endDate: '',
      type: 'all'
    });
  };

  // Toggle training selection for bulk editing
  const toggleTrainingSelection = (trainingId) => {
    setSelectedTrainings(prev => 
      prev.includes(trainingId) 
        ? prev.filter(id => id !== trainingId) 
        : [...prev, trainingId]
    );
  };

  // Select all filtered trainings
  const selectAllFiltered = () => {
    if (selectedTrainings.length === filteredTrainings.length && filteredTrainings.length > 0) {
      // Deselect all
      setSelectedTrainings([]);
    } else {
      // Select all filtered trainings
      setSelectedTrainings(filteredTrainings
        .filter(training => training && typeof training === 'object' && training.id)
        .map(training => training.id)
      );
    }
  };

  // Handle new training form changes
  const handleNewTrainingChange = (field, value) => {
    // For number fields, ensure we store numbers
    if (field.includes('Count') || field === 'totalParticipants' || field === 'trainingHours') {
      const numValue = value === '' ? '' : Number(value);
      setNewTrainingForm(prev => ({
        ...prev,
        [field]: numValue
      }));
    } else {
      setNewTrainingForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Add new training
  const addNewTraining = () => {
    // Validation
    if (!newTrainingForm.staffName.trim() || !newTrainingForm.staffId.trim()) {
      setError('Staff Name and Staff ID are required');
      return;
    }
    
    try {
      // Handle training not required field
      let trainingDue = newTrainingForm.trainingDue;
      let trainingDone = newTrainingForm.trainingDone;
      
      if (newTrainingForm.trainingNotRequired) {
        trainingDue = 'NA';
        trainingDone = 'NA';
      }
      
      const newTraining = {
        id: Date.now().toString(), // Simple ID generation
        staffName: newTrainingForm.staffName,
        staffId: newTrainingForm.staffId,
        department: newTrainingForm.department,
        trainingDue: trainingDue,
        trainingDone: trainingDone,
        trainingType: newTrainingForm.trainingType,
        trainingProvider: newTrainingForm.trainingProvider,
        certificateNumber: newTrainingForm.certificateNumber,
        targetAudience: newTrainingForm.targetAudience,
        trainerName: newTrainingForm.trainerName,
        doctorsCount: newTrainingForm.doctorsCount,
        techniciansCount: newTrainingForm.techniciansCount,
        engineersCount: newTrainingForm.engineersCount,
        nursingStaffCount: newTrainingForm.nursingStaffCount,
        othersCount: newTrainingForm.othersCount,
        totalParticipants: newTrainingForm.totalParticipants,
        trainingHours: newTrainingForm.trainingHours,
        trainingManhours: newTrainingForm.trainingManhours,
        trainingMandays: newTrainingForm.trainingMandays,
        createdAt: new Date().toISOString()
      };
      
      const updatedTrainings = [...staffTrainings, newTraining];
      if (saveStaffTrainings(updatedTrainings)) {
        setNewTrainingForm({
          staffName: '',
          staffId: '',
          department: '',
          trainingDue: '',
          trainingDone: new Date().toISOString().split('T')[0],
          trainingNotRequired: false,
          trainingType: '',
          trainingProvider: '',
          certificateNumber: '',
          targetAudience: '',
          trainerName: '',
          doctorsCount: '',
          techniciansCount: '',
          engineersCount: '',
          nursingStaffCount: '',
          othersCount: '',
          totalParticipants: '',
          trainingHours: '',
          trainingManhours: '',
          trainingMandays: ''
        });
      }
    } catch (error) {
      console.error('Failed to add new training:', error);
      setError('Failed to add new training: ' + error.message);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      // Prepare data for export
      const exportData = filteredTrainings.map((training, index) => ({
        'S.No.': index + 1,
        'Date': training.trainingDue || '',
        'Subject': training.trainingType || '',
        'Dept': training.department || '',
        'Target Audience': training.targetAudience || '',
        'Name of Trainer': training.trainerName || '',
        'Doctors': training.doctorsCount || 0,
        'Technicians': training.techniciansCount || 0,
        'Engineers': training.engineersCount || 0,
        'Nursing Staff': training.nursingStaffCount || 0,
        'Others': training.othersCount || 0,
        'No. of Participants (NOP)': training.totalParticipants || 0,
        'Training Hours (TH)': training.trainingHours || 0,
        'Training Manhours (TMH)': training.totalParticipants && training.trainingHours ? (parseFloat(training.totalParticipants) * parseFloat(training.trainingHours)).toFixed(2) : 0,
        'Training Mandays (TMD)': training.totalParticipants && training.trainingHours ? (parseFloat(training.totalParticipants) * parseFloat(training.trainingHours) / 8).toFixed(2) : 0
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Staff Training Records');
      
      // Export to file
      XLSX.writeFile(wb, 'staff_training_records.xlsx');
    } catch (error) {
      console.error('Failed to export to Excel:', error);
      setError('Failed to export to Excel: ' + error.message);
    }
  };

  // Get unique training types for filter dropdown
  const getUniqueTrainingTypes = () => {
    const types = new Set();
    staffTrainings.forEach(training => {
      if (training && typeof training === 'object' && training.trainingType) {
        types.add(training.trainingType);
      }
    });
    return Array.from(types);
  };

  // Get dashboard statistics
  const dashboardStats = getDashboardStats();
  const uniqueTrainingTypes = getUniqueTrainingTypes();

  if (loading) {
    return (
      <div className="staff-training-tracker-container">
        <div className="card">
          <h2 className="card-title">🎓 Staff Training Tracker</h2>
          <p>Loading staff training data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="staff-training-tracker-container">
        <div className="card">
          <h2 className="card-title">🎓 Staff Training Tracker</h2>
          <div className="error-message">❌ {error}</div>
          <button onClick={loadStaffTrainings} className="btn btn-primary">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="staff-training-tracker-container">
      <div className="card">
        <h2 className="card-title">🎓 Staff Training Tracker</h2>
        
        {currentUser && (
          <div className="user-badge">
            👤 Logged in as: {currentUser.name} ({currentUser.role})
          </div>
        )}
        
        {/* Dashboard Summary */}
        <div className="dashboard-summary">
          <h3>Staff Training Dashboard Summary</h3>
          
          <div className="summary-grid">
            <div className="summary-card total-assets">
              <div className="summary-value">{staffTrainings.length}</div>
              <div className="summary-label">Total Training Records</div>
            </div>
            <div className="summary-card filtered-assets">
              <div className="summary-value">{filteredTrainings.length}</div>
              <div className="summary-label">Filtered Records</div>
            </div>
            <div className="summary-card due-today">
              <div className="summary-value">{dashboardStats.training.dueToday}</div>
              <div className="summary-label">Due Today</div>
            </div>
            <div className="summary-card this-month-done">
              <div className="summary-value">{dashboardStats.training.thisMonthDone}</div>
              <div className="summary-label">This Month Done</div>
            </div>
            <div className="summary-card this-month-due">
              <div className="summary-value">{dashboardStats.training.thisMonthDue}</div>
              <div className="summary-label">This Month Due</div>
            </div>
            <div className="summary-card next-month-due">
              <div className="summary-value">{dashboardStats.training.nextMonthDue}</div>
              <div className="summary-label">Next Month Due</div>
            </div>
            <div className="summary-card future-due">
              <div className="summary-value">{dashboardStats.training.futureDue}</div>
              <div className="summary-label">Future Due</div>
            </div>
            <div className="summary-card completed">
              <div className="summary-value">{dashboardStats.training.completed}</div>
              <div className="summary-label">Completed</div>
            </div>
            <div className="summary-card training-overdue">
              <div className="summary-value">{dashboardStats.training.overdue}</div>
              <div className="summary-label">Overdue</div>
            </div>
            <div className="summary-card training-not-required">
              <div className="summary-value">{dashboardStats.training.notRequired}</div>
              <div className="summary-label">Not Required</div>
            </div>
          </div>
          
          {/* Training Statistics by Type */}
          <h4>Training Statistics by Type</h4>
          <div className="summary-grid">
            {Object.entries(dashboardStats.training.byType).map(([type, stats]) => (
              <div key={type} className="summary-card">
                <div className="summary-value">{stats.total}</div>
                <div className="summary-label">{type} (Total)</div>
                <div className="summary-details">
                  <span>Due: {stats.dueToday}</span>
                  <span>Comp: {stats.completed}</span>
                  <span>Over: {stats.overdue}</span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Training Statistics by Department */}
          <h4>Training Statistics by Department</h4>
          <div className="summary-grid">
            {Object.entries(dashboardStats.training.byDepartment).map(([dept, stats]) => (
              <div key={dept} className="summary-card">
                <div className="summary-value">{stats.total}</div>
                <div className="summary-label">{dept} (Total)</div>
                <div className="summary-details">
                  <span>Due: {stats.dueToday}</span>
                  <span>Comp: {stats.completed}</span>
                  <span>Over: {stats.overdue}</span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Participant Statistics */}
          <h4>Participant Statistics</h4>
          <div className="summary-grid">
            <div className="summary-card total-participants">
              <div className="summary-value">{dashboardStats.participants.totalParticipants}</div>
              <div className="summary-label">Total Participants</div>
            </div>
            <div className="summary-card doctors">
              <div className="summary-value">{dashboardStats.participants.doctors}</div>
              <div className="summary-label">Doctors</div>
            </div>
            <div className="summary-card technicians">
              <div className="summary-value">{dashboardStats.participants.technicians}</div>
              <div className="summary-label">Technicians</div>
            </div>
            <div className="summary-card engineers">
              <div className="summary-value">{dashboardStats.participants.engineers}</div>
              <div className="summary-label">Engineers</div>
            </div>
            <div className="summary-card nursing-staff">
              <div className="summary-value">{dashboardStats.participants.nursingStaff}</div>
              <div className="summary-label">Nursing Staff</div>
            </div>
            <div className="summary-card others">
              <div className="summary-value">{dashboardStats.participants.others}</div>
              <div className="summary-label">Others</div>
            </div>
          </div>
        </div>
        
        {/* Add New Training Section */}
        <div className="add-new-section">
          <h3>Add New Staff Training</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Training Done Date:</label>
              <input
                type="date"
                value={newTrainingForm.trainingDone}
                onChange={(e) => handleNewTrainingChange('trainingDone', e.target.value)}
                placeholder="Select date"
              />
            </div>
            <div className="form-group">
              <label>Staff Name*:</label>
              <input
                type="text"
                value={newTrainingForm.staffName}
                onChange={(e) => handleNewTrainingChange('staffName', e.target.value)}
                placeholder="Enter staff name"
              />
            </div>
            <div className="form-group">
              <label>Staff ID*:</label>
              <input
                type="text"
                value={newTrainingForm.staffId}
                onChange={(e) => handleNewTrainingChange('staffId', e.target.value)}
                placeholder="Enter staff ID"
              />
            </div>
            <div className="form-group">
              <label>Department:</label>
              <input
                type="text"
                value={newTrainingForm.department}
                onChange={(e) => handleNewTrainingChange('department', e.target.value)}
                placeholder="Enter department"
              />
            </div>


            <div className="form-group">
              <label>Training Type/Subject:</label>
              <input
                type="text"
                value={newTrainingForm.trainingType}
                onChange={(e) => handleNewTrainingChange('trainingType', e.target.value)}
                placeholder="e.g., Operation, Maintenance, Safety"
              />
            </div>
            <div className="form-group">
              <label>Training Provider:</label>
              <input
                type="text"
                value={newTrainingForm.trainingProvider}
                onChange={(e) => handleNewTrainingChange('trainingProvider', e.target.value)}
                placeholder="e.g., Medskills, Zydus Training"
              />
            </div>

            <div className="form-group">
              <label>Target Audience:</label>
              <input
                type="text"
                value={newTrainingForm.targetAudience}
                onChange={(e) => handleNewTrainingChange('targetAudience', e.target.value)}
                placeholder="e.g., All Staff, Technical Team"
              />
            </div>
            <div className="form-group">
              <label>Name of Trainer:</label>
              <input
                type="text"
                value={newTrainingForm.trainerName}
                onChange={(e) => handleNewTrainingChange('trainerName', e.target.value)}
                placeholder="Enter trainer name"
              />
            </div>
            <div className="form-group">
              <label>Doctors:</label>
              <input
                type="number"
                value={newTrainingForm.doctorsCount}
                onChange={(e) => handleNewTrainingChange('doctorsCount', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <label>Technicians:</label>
              <input
                type="number"
                value={newTrainingForm.techniciansCount}
                onChange={(e) => handleNewTrainingChange('techniciansCount', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <label>Engineers:</label>
              <input
                type="number"
                value={newTrainingForm.engineersCount}
                onChange={(e) => handleNewTrainingChange('engineersCount', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <label>Nursing Staff:</label>
              <input
                type="number"
                value={newTrainingForm.nursingStaffCount}
                onChange={(e) => handleNewTrainingChange('nursingStaffCount', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <label>Others:</label>
              <input
                type="number"
                value={newTrainingForm.othersCount}
                onChange={(e) => handleNewTrainingChange('othersCount', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <label>No. of Participants (NOP):</label>
              <input
                type="number"
                value={newTrainingForm.totalParticipants}
                onChange={(e) => handleNewTrainingChange('totalParticipants', e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="form-group">
              <label>Training Hours (TH):</label>
              <input
                type="number"
                value={newTrainingForm.trainingHours}
                onChange={(e) => handleNewTrainingChange('trainingHours', e.target.value)}
                placeholder="0"
                step="0.5"
              />
            </div>
            <div className="form-group">
              <label>Training Manhours (TMH):</label>
              <input
                type="text"
                value={newTrainingForm.totalParticipants && newTrainingForm.trainingHours ? (parseFloat(newTrainingForm.totalParticipants) * parseFloat(newTrainingForm.trainingHours)).toFixed(2) : ''}
                readOnly
              />
              <small>Calculated: NOP × TH</small>
            </div>
            <div className="form-group">
              <label>Training Mandays (TMD):</label>
              <input
                type="text"
                value={newTrainingForm.totalParticipants && newTrainingForm.trainingHours ? (parseFloat(newTrainingForm.totalParticipants) * parseFloat(newTrainingForm.trainingHours) / 8).toFixed(2) : ''}
                readOnly
              />
              <small>Calculated: TMH ÷ 8</small>
            </div>
          </div>
          <div className="add-new-actions">
            <button 
              className="btn btn-success"
              onClick={addNewTraining}
            >
              Add Training Record
            </button>
          </div>
        </div>
        
        {/* Filter Section */}
        <div className="filter-section">
          <h3>Filters</h3>
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="dateFilter">Date Filter:</label>
              <select 
                id="dateFilter"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                disabled={dateRangeFilter.startDate || dateRangeFilter.endDate}
              >
                <option value="all">All Dates</option>
                <option value="dueToday">Due Today</option>
                <option value="thisMonthDone">This Month Done</option>
                <option value="thisMonthDue">This Month Due</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="trainingTypeFilter">Training Type:</label>
              <select 
                id="trainingTypeFilter"
                value={trainingTypeFilter}
                onChange={(e) => setTrainingTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                {uniqueTrainingTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="search">Search:</label>
              <input
                type="text"
                id="search"
                placeholder="Search across all fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="filter-group">
              <label>&nbsp;</label>
              <button 
                className="btn btn-secondary"
                onClick={clearDateRangeFilter}
              >
                Clear Date Range
              </button>
            </div>
          </div>
          
          {/* Date Range Filter */}
          <div className="filter-row">
            <div className="filter-group">
              <label htmlFor="startDate">Start Date:</label>
              <input
                type="date"
                id="startDate"
                value={dateRangeFilter.startDate}
                onChange={(e) => setDateRangeFilter(prev => ({...prev, startDate: e.target.value}))}
                disabled={dateFilter !== 'all'}
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="endDate">End Date:</label>
              <input
                type="date"
                id="endDate"
                value={dateRangeFilter.endDate}
                onChange={(e) => setDateRangeFilter(prev => ({...prev, endDate: e.target.value}))}
                disabled={dateFilter !== 'all'}
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="dateRangeType">Filter Type:</label>
              <select
                id="dateRangeType"
                value={dateRangeFilter.type}
                onChange={(e) => setDateRangeFilter(prev => ({...prev, type: e.target.value}))}
                disabled={dateFilter !== 'all'}
              >
                <option value="all">All Dates</option>
                <option value="due">Due Dates Only</option>
                <option value="done">Done Dates Only</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="trainings-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>Staff Training Records ({filteredTrainings.length})</h3>
            <button className="btn btn-success" onClick={exportToExcel}>
              Export to Excel
            </button>
          </div>
          
          {filteredTrainings.length > 0 ? (
            <div className="table-container">
              <table className="trainings-table">
                <thead>
                  <tr>
                    <th>S.No.</th>
                    <th>Date</th>
                    <th>Subject</th>
                    <th>Dept</th>
                    <th>Target Audience</th>
                    <th>Name of Trainer</th>
                    <th>Doctors</th>
                    <th>Technicians</th>
                    <th>Engineers</th>
                    <th>Nursing Staff</th>
                    <th>Others</th>
                    <th>No. of Participants (NOP)</th>
                    <th>Training Hours (TH)</th>
                    <th>Training Manhours (TMH)</th>
                    <th>Training Mandays (TMD)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrainings.map((training, index) => {
                    // Additional safety check
                    if (!training || typeof training !== 'object') {
                      return null;
                    }
                    
                    return (
                      // Display row with requested format
                      <tr key={training.id || index}>
                        <td>{index + 1}</td>
                        <td>{training.trainingDue || ''}</td>
                        <td>{training.trainingType || ''}</td>
                        <td>{training.department || ''}</td>
                        <td>{training.targetAudience || ''}</td>
                        <td>{training.trainerName || ''}</td>
                        <td>{training.doctorsCount || '0'}</td>
                        <td>{training.techniciansCount || '0'}</td>
                        <td>{training.engineersCount || '0'}</td>
                        <td>{training.nursingStaffCount || '0'}</td>
                        <td>{training.othersCount || '0'}</td>
                        <td>{training.totalParticipants || '0'}</td>
                        <td>{training.trainingHours || '0'}</td>
                        <td>{training.totalParticipants && training.trainingHours ? (parseFloat(training.totalParticipants) * parseFloat(training.trainingHours)).toFixed(2) : '0.00'}</td>
                        <td>{training.totalParticipants && training.trainingHours ? (parseFloat(training.totalParticipants) * parseFloat(training.trainingHours) / 8).toFixed(2) : '0.00'}</td>
                      </tr>
                    );
                  }).filter(row => row !== null)}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-trainings">
              No staff training records found matching your criteria.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffTrainingTracker;